import type { BrowserAuthManager } from "./auth";

/** Shared native sign-in and recovery UI. */
export function authenticationForm(
  auth: BrowserAuthManager,
  onSignedIn: () => Promise<void>,
  initialError?: string | null,
): HTMLFormElement {
  const form = document.createElement("form");
  form.className = "authentication-form";
  const field = (labelText: string, name: string, type: string, autocomplete: string) => {
    const label = document.createElement("label");
    label.textContent = labelText;
    const input = document.createElement("input");
    Object.assign(input, { name, type, autocomplete, required: true });
    label.append(input);
    form.append(label);
    return input;
  };
  const email = field("Email", "email", "email", "username");
  const password = field("Password", "password", "password", "current-password");
  const mfa = document.createElement("div");
  mfa.className = "authentication-mfa";
  form.append(mfa);
  const code = field("Authenticator code", "code", "text", "one-time-code");
  code.inputMode = "numeric";
  const error = document.createElement("p");
  error.setAttribute("role", "alert");
  const notice = document.createElement("p");
  notice.setAttribute("role", "status");
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "button button--white";
  const recovery = document.createElement("button");
  recovery.type = "button";
  recovery.className = "button button--white";
  const help = document.createElement("p");
  help.textContent = "To create an account, open the link in your invitation email.";
  const backup = document.createElement("button");
  backup.type = "button";
  backup.className = "button button--white";
  let usingBackup = false;
  let mode: "signin" | "reset" | "mfa" | "codes" = "signin";
  const copyButton = (label: string, value: string) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "button button--white";
    button.textContent = label;
    button.addEventListener("click", () => {
      void (async () => {
        try {
          await navigator.clipboard.writeText(value);
          notice.textContent = "Copied.";
        } catch {
          error.textContent = "Copying is unavailable. Select and copy the text instead.";
        }
      })();
    });
    return button;
  };
  const renderMfa = () => {
    mfa.replaceChildren();
    const instructions = document.createElement("p");
    const setup = auth.mfaChallenge?.setup;
    if (mode === "codes") {
      instructions.textContent =
        "Save these backup codes somewhere safe. Each can replace a lost authenticator. They will only be shown once.";
      const codes = document.createElement("pre");
      codes.textContent = auth.recoveryCodes.join("\n");
      mfa.append(instructions, codes, copyButton("Copy backup codes", codes.textContent));
    } else if (setup) {
      instructions.textContent =
        "Add Groundspark to Authy, Google Authenticator or another authenticator app. Scan this QR code, or copy the setup key into your authenticator on this phone. Then enter its six-digit code.";
      const qr = document.createElement("img");
      qr.src = `data:image/png;base64,${setup.qrCode}`;
      qr.alt = "Scan with your authenticator to set up Groundspark";
      qr.width = qr.height = 220;
      const key = document.createElement("code");
      key.textContent = setup.secret;
      mfa.append(instructions, qr, key, copyButton("Copy setup key", setup.secret));
    } else if (mode === "mfa") {
      instructions.textContent = usingBackup
        ? "Enter a backup code to set up a replacement authenticator. This signs out existing sessions. Confirming the replacement invalidates your remaining backup codes."
        : "Open your authenticator app and enter the current six-digit code.";
      mfa.append(instructions);
    }
  };
  const update = () => {
    email.parentElement!.hidden = mode === "mfa" || mode === "codes";
    email.required = mode === "signin" || mode === "reset";
    password.parentElement!.hidden = mode !== "signin";
    password.required = mode === "signin";
    code.parentElement!.hidden = mode !== "mfa";
    code.required = mode === "mfa";
    code.inputMode = usingBackup ? "text" : "numeric";
    code.autocomplete = usingBackup ? "off" : "one-time-code";
    code.maxLength = usingBackup ? 64 : 6;
    code.pattern = usingBackup ? "[a-fA-F0-9 \\-]{32,64}" : "[0-9]{6}";
    code.parentElement!.firstChild!.textContent = usingBackup
      ? "Backup code"
      : "Authenticator code";
    submit.textContent =
      mode === "reset"
        ? "Send reset link"
        : mode === "mfa"
          ? "Verify code"
          : mode === "codes"
            ? "I have saved these codes"
            : "Sign in";
    recovery.textContent = mode === "signin" ? "Forgot password?" : "Back to sign in";
    backup.hidden = mode !== "mfa" || auth.mfaChallenge?.setup !== null;
    backup.textContent = usingBackup ? "Use authenticator code" : "Use a backup code";
    help.hidden = mode === "mfa" || mode === "codes";
    mfa.hidden = mode !== "mfa" && mode !== "codes";
    error.textContent = "";
    renderMfa();
  };
  recovery.addEventListener("click", () => {
    mode = mode === "signin" ? "reset" : "signin";
    auth.cancelSignIn();
    usingBackup = false;
    password.value = code.value = "";
    notice.textContent = "";
    update();
  });
  backup.addEventListener("click", () => {
    usingBackup = !usingBackup;
    code.value = "";
    update();
    code.focus();
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (submit.disabled || !form.reportValidity()) return;
    submit.disabled = recovery.disabled = backup.disabled = true;
    error.textContent = notice.textContent = "";
    void (async () => {
      try {
        if (mode === "reset") {
          await auth.requestPasswordReset(email.value.trim());
          notice.textContent =
            "If this address belongs to a verified account, a password reset link will arrive shortly.";
        } else {
          if (mode === "codes") auth.acknowledgeRecoveryCodes();
          else if (mode === "mfa") await auth.verifyMfa(code.value.trim());
          else await auth.signIn(email.value.trim(), password.value);
          if (auth.mfaChallenge !== null) {
            mode = "mfa";
            usingBackup = false;
            code.value = "";
            update();
            code.focus();
          } else if (auth.recoveryCodes.length > 0) {
            mode = "codes";
            update();
          } else {
            await onSignedIn();
          }
        }
      } catch (failure) {
        error.textContent = failure instanceof Error ? failure.message : String(failure);
      } finally {
        password.value = "";
        submit.disabled = recovery.disabled = backup.disabled = false;
      }
    })();
  });
  update();
  error.textContent = initialError ?? "";
  form.append(error, notice, submit, recovery, backup, help);
  return form;
}
